import React from "react";
import "@/App.css";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useParams,
} from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import AreaGuard from "@/components/AreaGuard";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import AreaPicker from "@/pages/AreaPicker";
import Dashboard from "@/pages/Dashboard";
import Customers from "@/pages/Customers";
import CustomerDetail from "@/pages/CustomerDetail";
import Orders from "@/pages/Orders";
import Pipeline from "@/pages/Pipeline";
import Vendors from "@/pages/Vendors";
import VendorDetail from "@/pages/VendorDetail";
import Products from "@/pages/Products";
import Pricing from "@/pages/Pricing";
import Invoices from "@/pages/Invoices";
import InvoiceBuilder from "@/pages/InvoiceBuilder";
import InvoiceView from "@/pages/InvoiceView";
import ARFollowUps from "@/pages/ARFollowUps";
import Settings from "@/pages/Settings";
import PublicInvoice from "@/pages/PublicInvoice";
import AdminRoles from "@/pages/AdminRoles";
import AdminSettings from "@/pages/AdminSettings";
import InventoryDashboard from "@/pages/inventory/InventoryDashboard";
import InventoryItems from "@/pages/inventory/Items";
import InventoryStock from "@/pages/inventory/Stock";
import InventoryRecipes from "@/pages/inventory/Recipes";
import InventoryBatchCalculator from "@/pages/inventory/BatchCalculator";
import InventoryProductionRuns from "@/pages/inventory/ProductionRuns";
import InventoryProductionWizard from "@/pages/inventory/ProductionRunWizard";
import InventoryProductionRunDetail from "@/pages/inventory/ProductionRunDetail";
import InventoryRequisitions from "@/pages/inventory/Requisitions";
import InventoryRequisitionDetail from "@/pages/inventory/RequisitionDetail";
import InventoryProcurement from "@/pages/inventory/Procurement";
import InventoryPurchaseOrders from "@/pages/inventory/PurchaseOrders";
import InventoryIso from "@/pages/inventory/IsoPage";

function VendorRedirect() {
  const { id } = useParams();
  return <Navigate to={`/inventory/vendors/${id}`} replace />;
}

function AppRouter() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/share/:token" element={<PublicInvoice />} />

      {/* Post-login landing — area picker (auto-redirects single-area users) */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AreaPicker />
          </ProtectedRoute>
        }
      />

      {/* Sales workspace */}
      <Route
        element={
          <ProtectedRoute>
            <AreaGuard area="sales">
              <Layout />
            </AreaGuard>
          </ProtectedRoute>
        }
      >
        <Route path="/sales" element={<Navigate to="/sales/dashboard" replace />} />
        <Route path="/sales/dashboard" element={<Dashboard />} />
        <Route path="/sales/customers" element={<Customers />} />
        <Route path="/sales/customers/:id" element={<CustomerDetail />} />
        <Route path="/sales/pipeline" element={<Pipeline />} />
        <Route path="/sales/orders" element={<Orders />} />
      </Route>

      {/* Finance / Invoice workspace */}
      <Route
        element={
          <ProtectedRoute>
            <AreaGuard area="invoice">
              <Layout />
            </AreaGuard>
          </ProtectedRoute>
        }
      >
        <Route path="/invoices" element={<Invoices />} />
        <Route path="/invoices/follow-ups" element={<ARFollowUps />} />
        <Route path="/invoices/new" element={<InvoiceBuilder />} />
        <Route path="/invoices/:id/edit" element={<InvoiceBuilder />} />
        <Route path="/invoices/:id" element={<InvoiceView />} />
        <Route path="/products" element={<Products />} />
        <Route path="/sales/pricing" element={<Pricing />} />
        <Route path="/settings" element={<Settings />} />
      </Route>

      {/* Inventory / ERP workspace */}
      <Route
        element={
          <ProtectedRoute>
            <AreaGuard area="inventory">
              <Layout />
            </AreaGuard>
          </ProtectedRoute>
        }
      >
        <Route path="/inventory" element={<InventoryDashboard />} />
        <Route path="/inventory/items" element={<InventoryItems />} />
        <Route path="/inventory/stock" element={<InventoryStock />} />
        <Route path="/inventory/recipes" element={<InventoryRecipes />} />
        <Route path="/inventory/batch-calculator" element={<InventoryBatchCalculator />} />
        <Route path="/inventory/production-runs" element={<InventoryProductionRuns />} />
        <Route path="/inventory/production-wizard" element={<InventoryProductionWizard />} />
        <Route path="/inventory/production-wizard/:id" element={<InventoryProductionWizard />} />
        <Route path="/inventory/production-runs/:id" element={<InventoryProductionRunDetail />} />
        <Route path="/inventory/requisitions" element={<InventoryRequisitions />} />
        <Route path="/inventory/requisitions/:id" element={<InventoryRequisitionDetail />} />
        <Route path="/inventory/purchase-orders" element={<InventoryPurchaseOrders />} />
        <Route path="/inventory/vendors" element={<Vendors />} />
        <Route path="/inventory/vendors/:id" element={<VendorDetail />} />
        <Route path="/inventory/procurement" element={<InventoryProcurement />} />
        <Route path="/inventory/iso" element={<InventoryIso />} />
      </Route>

      {/* Admin Settings (admin only; also enforced at the API layer) */}
      <Route
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route path="/admin/settings" element={<AdminSettings />}>
          <Route index element={<Navigate to="users" replace />} />
          <Route path="users" element={<AdminRoles />} />
        </Route>
      </Route>
      <Route path="/admin/roles" element={<Navigate to="/admin/settings/users" replace />} />

      {/* Legacy top-level routes → redirect to new prefix */}
      <Route path="/dashboard" element={<Navigate to="/sales/dashboard" replace />} />
      <Route path="/customers" element={<Navigate to="/sales/customers" replace />} />
      <Route path="/pipeline" element={<Navigate to="/sales/pipeline" replace />} />
      <Route path="/orders" element={<Navigate to="/sales/orders" replace />} />
      <Route path="/vendors" element={<Navigate to="/inventory/vendors" replace />} />
      {/* Vendors moved from Sales to Inventory */}
      <Route path="/sales/vendors" element={<Navigate to="/inventory/vendors" replace />} />
      <Route path="/sales/vendors/:id" element={<VendorRedirect />} />

      {/* Fallback → area picker */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
